import {ChangeDetectionStrategy, Component, OnInit, computed, input, output} from '@angular/core'
import {DirectoryPro, Metric} from '../pro.directory.types'
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons/faCaretDown'
import {faLocationDot} from '@fortawesome/pro-solid-svg-icons/faLocationDot'
import {faPlus} from '@fortawesome/pro-solid-svg-icons/faPlus'
import {faUser} from '@fortawesome/pro-solid-svg-icons/faUser'
import {PUBLIC_ROUTES} from '../../../routing/public.routes'
import {LinkService, OTHER_OFFERING_ID} from '../../../../core/i18n/link.service'
import {faClock} from '@fortawesome/pro-solid-svg-icons/faClock'
import {faLanguage} from '@fortawesome/pro-solid-svg-icons/faLanguage'
import {faGraduationCap} from '@fortawesome/pro-solid-svg-icons/faGraduationCap'
import {faSun} from '@fortawesome/pro-solid-svg-icons/faSun'
import {GalleryImage} from '../../gallery/gallery.component'
import {MobiliarLogoComponent} from '../../branding/mobiliar-logo/mobiliar-logo.component'

@Component({
  selector: 'bgo-pro-directory-card',
  templateUrl: './pro-directory-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {ngSkipHydration: 'true'}, //eslint-disable-line @angular-eslint/no-host-metadata-property
})
export class ProDirectoryCardComponent implements OnInit {
  pro = input.required<DirectoryPro>()
  metrics = computed(() => {
    const results: Metric[] = []
    const pro = this.pro()

    if (pro.recommendingAgency?.agencyName) {
      results.push({
        label: {
          localizedValue: 'proDirectoryCard.recommendedBy',
        },
        suffix: {
          value: `| ${pro.recommendingAgency.agencyName}`,
          icon: {component: MobiliarLogoComponent, inputs: {size: '3xs'}},
        },
        info: {
          localizedValue: 'proDirectoryCard.recommendedByInfo',
          params: {agency: pro.recommendingAgency.agencyName},
        },
      })
    }

    if (pro.numApprentices > 1) {
      results.push({
        label: {localizedValue: 'proDirectoryCard.trainingCompanyLabel'},
        icon: faGraduationCap,
        info: {localizedValue: 'proDirectoryCard.trainingCompanyInfo'},
      })
    }

    if (pro.responseTimeInHours && pro.responseTimeInHours <= 5) {
      results.push({
        label: {localizedValue: 'proDirectoryCard.answersFastLabel'},
        icon: faClock,
        info: {localizedValue: 'proDirectoryCard.answersFastInfo'},
      })
    }

    if (pro.additionallySpokenLanguages.length > 1) {
      results.push({
        label: {localizedValue: 'proDirectoryCard.multilingualLabel'},
        icon: faLanguage,
      })
    }

    if (pro.isErneuerbarheizenNetwork) {
      results.push({
        label: {localizedValue: 'proDirectoryCard.renewableHeatingLabel'},
        icon: faSun,
        info: {
          localizedValue: 'proDirectoryCard.renewableHeatingInfo',
        },
      })
    }

    for (const association of pro.associations) {
      results.push({
        label: {localizedValue: `proDirectoryCard.associations.${association.toLowerCase()}`},
        info: {
          localizedValue: 'proDirectoryCard.associationInfo',
          params: {associations: pro.associations.join(', ')},
        },
      })
    }

    return results
  })

  gallery = computed(() => {
    const gallery: GalleryImage[] = []
    const pro = this.pro()
    if (pro.logo?.url) {
      gallery.push({url: pro.logo.url})
    }

    if (pro.galleryImages.length) {
      gallery.push(...pro.galleryImages.map(i => ({url: i.url!})))
    }

    return gallery
  })
  navigated = output()

  protected readonly locationIcon = faLocationDot
  protected readonly profileIcon = faUser
  protected readonly dropdownIcon = faCaretDown
  protected readonly plusIcon = faPlus

  protected jobRequestRoute!: string
  protected proProfileRoute!: string
  protected coverImageUrl?: string
  protected logoImageUrl?: string

  constructor(private readonly linkService: LinkService) {}

  ngOnInit(): void {
    this.proProfileRoute = this.linkService.localizedPathTo(PUBLIC_ROUTES.PRO_PROFILE, {
      routeParams: {
        slug: this.pro().slug,
      },
    })
    this.coverImageUrl = this.pro().coverImage?.url
    this.logoImageUrl = this.pro().logo?.url
    this.jobRequestRoute = this.linkService.localizedPathTo(PUBLIC_ROUTES.JOB_REQUEST, {
      routeParams: {
        offeringId: OTHER_OFFERING_ID,
      },
    })
  }

  navigate(event: Event): void {
    this.navigated.emit()
    event.stopPropagation()
  }

  getFirstLetter(name: string): string {
    return name.charAt(0).toUpperCase()
  }

  showLocationsDropdown(event: MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
  }
}
